import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart3,
  ClipboardList,
  CreditCard,
  Download,
  Eye,
  IndianRupee,
  MapPin,
  Pencil,
  Printer,
  RefreshCw,
  ShoppingBag,
  Trash2,
  User,
} from 'lucide-react';
import { fetchAllOrders } from "../../store";
import { updateOrderStatus } from '../../store';
import './OrderManagement.css';

const iconProps = { size: 18, strokeWidth: 1.75 };

const OrderManagement = () => {
  const dispatch = useDispatch();
  const ordersFromStore = useSelector((state) => state.orders.items || []);

  // Map orders to UI-friendly format
  const mapOrders = (ordersArr) =>
    (ordersArr || []).map((order) => ({
      id: order._id,
      orderId: order.orderId,
      customer: {
        name: order.billingInfo?.fullName || "",
        email: order.billingInfo?.email || "",
        phone: order.billingInfo?.phone || "",
      },
      items: order.items || [],
      totals: order.totals || {},
      orderStatus: order.orderStatus,
      isPaid: order.isPaid,
      paymentStatus: order.payment?.status === "Paid" ? "Paid" : (order.payment?.status || "Unpaid"),
      payment: order.payment,
      shippingInfo: order.shippingInfo,
      date: order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "",
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
    }));

  const [orders, setOrders] = useState(mapOrders(ordersFromStore));
  const [filteredOrders, setFilteredOrders] = useState(orders);
  const [currentViewingOrder, setCurrentViewingOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    payment: '',
    date: '',
    search: ''
  });
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0
  });

  // Fetch orders on mount
  useEffect(() => {
    dispatch(fetchAllOrders());
  }, [dispatch]);

  // Update orders whenever ordersFromStore changes
  useEffect(() => {
    setOrders(mapOrders(ordersFromStore));
    console.log(ordersFromStore);
    
  }, [ordersFromStore]);

  // Update stats whenever orders change
  useEffect(() => {
    const newStats = {
      pending: orders.filter(o => o.orderStatus === 'Pending').length,
      processing: orders.filter(o => o.orderStatus === 'Processing').length,
      shipped: orders.filter(o => o.orderStatus === 'Shipped').length,
      delivered: orders.filter(o => o.orderStatus === 'Delivered').length
    };
    setStats(newStats);
  }, [orders]);

  // Filter orders based on current filters
  useEffect(() => {
    let filtered = orders.filter(order => {
      const matchesStatus = !filters.status || order.orderStatus === filters.status;
      const matchesPayment = !filters.payment ||
        (filters.payment === 'paid' && order.isPaid) ||
        (filters.payment === 'unpaid' && !order.isPaid);
      const matchesSearch = !filters.search ||
        (order.orderId && order.orderId.toLowerCase().includes(filters.search.toLowerCase())) ||
        (order.customer.name && order.customer.name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (order.customer.email && order.customer.email.toLowerCase().includes(filters.search.toLowerCase()));

      return matchesStatus && matchesPayment && matchesSearch;
    });
    setFilteredOrders(filtered);
  }, [orders, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const viewOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setCurrentViewingOrder(order);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentViewingOrder(null);
  };

  // Get valid next statuses based on current status (order workflow validation)
  const getValidNextStatuses = (currentStatus) => {
    const transitions = {
      'Pending': ['Processing', 'Cancelled'],
      'Processing': ['Shipped', 'Cancelled'],
      'Shipped': ['Delivered', 'Cancelled'],
      'Delivered': [], // Terminal state
      'Cancelled': []  // Terminal state
    };

    // Always include current status in dropdown
    const validStatuses = [currentStatus];
    const nextStatuses = transitions[currentStatus] || [];

    return [...validStatuses, ...nextStatuses];
  };

  const updateOrderState = (newStatus) => {
    if (!currentViewingOrder) return;

    const currentStatus = currentViewingOrder.orderStatus;

    // Validate transition
    const validStatuses = getValidNextStatuses(currentStatus);
    if (!validStatuses.includes(newStatus)) {
      alert(`Invalid status transition: ${currentStatus} → ${newStatus}`);
      return;
    }

    // Same status selected, no change needed
    if (currentStatus === newStatus) return;

    // Confirmation for Cancelled status
    if (newStatus === 'Cancelled') {
      const confirmMessage = currentViewingOrder.isPaid
        ? `Are you sure you want to cancel this order?\n\nOrder: ${currentViewingOrder.orderId}\nAmount: ₹${currentViewingOrder.totals.grandTotal.toLocaleString()}\n\nThis order is PAID. Cancellation will require processing a refund.`
        : `Are you sure you want to cancel this order?\n\nOrder: ${currentViewingOrder.orderId}\n\nThis action cannot be undone.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    // Dispatch to backend
    dispatch(updateOrderStatus({ orderId: currentViewingOrder.id, status: newStatus }))

    // Update local state
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === currentViewingOrder.id
          ? {
              ...order,
              orderStatus: newStatus,
              paymentStatus: newStatus === 'Cancelled' && order.isPaid ? 'Refunded' : order.paymentStatus
            }
          : order
      )
    );

    // Update current viewing order
    setCurrentViewingOrder(prev => ({
      ...prev,
      orderStatus: newStatus,
      paymentStatus: newStatus === 'Cancelled' && prev.isPaid ? 'Refunded' : prev.paymentStatus
    }));
  };

  const deleteOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    if (window.confirm(`Are you sure you want to cancel order "${order.orderId}"? This action cannot be undone.`)) {
      setOrders(prevOrders => 
        prevOrders.map(o => 
          o.id === orderId 
            ? { 
                ...o, 
                orderStatus: 'Cancelled',
                paymentStatus: o.isPaid ? 'Refunded' : o.paymentStatus
              }
            : o
        )
      );
    }
  };

  const editOrderStatus = (orderId) => {
    viewOrder(orderId);
  };

  const handleExport = () => {
    alert('Export functionality would be implemented here. Orders data would be exported to CSV/Excel.');
  };

  const handleGenerateReport = () => {
    alert('Report generation functionality would be implemented here. Detailed sales and order reports would be generated.');
  };

  const handlePrintInvoice = () => {
    if (currentViewingOrder) {
      alert(`Print invoice functionality for order ${currentViewingOrder.orderId} would be implemented here.`);
    }
  };

  return (
    <div className="om-order-management">
      {/* Main Content */}
      <div className="om-main-container">
        {/* Page Header */}
        <div className="om-page-header">
          <h1 className="om-page-title">Order Management</h1>
          <div className="om-page-actions">
            <button type="button" className="om-btn-secondary" onClick={handleExport}>
              <Download {...iconProps} className="om-btn-leading-icon" aria-hidden />
              Export Orders
            </button>
            <button type="button" className="om-btn-primary" onClick={handleGenerateReport}>
              <BarChart3 {...iconProps} className="om-btn-leading-icon" aria-hidden />
              Generate Report
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="om-stats-grid">
          <div className="om-stats-card">
            <div className="om-stats-value">{stats.pending}</div>
            <div className="om-stats-label">Pending Orders</div>
          </div>
          <div className="om-stats-card">
            <div className="om-stats-value">{stats.processing}</div>
            <div className="om-stats-label">Processing</div>
          </div>
          <div className="om-stats-card">
            <div className="om-stats-value">{stats.shipped}</div>
            <div className="om-stats-label">Shipped</div>
          </div>
          <div className="om-stats-card">
            <div className="om-stats-value">{stats.delivered}</div>
            <div className="om-stats-label">Delivered</div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="om-filters-section">
          <div className="om-filters-row">
            <div className="om-filter-group">
              <label className="om-filter-label">Order Status</label>
              <select 
                className="om-filter-select" 
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="om-filter-group">
              <label className="om-filter-label">Payment Status</label>
              <select 
                className="om-filter-select"
                value={filters.payment}
                onChange={(e) => handleFilterChange('payment', e.target.value)}
              >
                <option value="">All Payments</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div className="om-filter-group">
              <label className="om-filter-label">Date Range</label>
              <select 
                className="om-filter-select"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            <div className="om-filter-group">
              <label className="om-filter-label">Search Orders</label>
              <input 
                type="text" 
                className="om-search-filter" 
                placeholder="Order ID, customer name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="om-orders-table-container">
          <div className="om-table-header">
            <div className="om-table-title">All Orders</div>
            <div className="om-table-count">Showing {filteredOrders.length.toLocaleString()} orders</div>
          </div>
          
          <table className="om-orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.id} className="om-table-row">
                  <td className="om-order-id">{order.orderId}</td>
                  <td>
                    <div className="om-customer-info">
                      <div className="om-customer-name">{order.customer.name}</div>
                      <div className="om-customer-email">{order.customer.email}</div>
                    </div>
                  </td>
                  <td>
                    <div className="om-order-items">{order.items.length} items</div>
                  </td>
                  <td>
                    <div className="om-order-total">₹{order.totals.grandTotal?.toLocaleString()}</div>
                  </td>
                  <td>
                    <span className={`om-status-badge om-status-${(order.orderStatus || '').toLowerCase()}`}>
                      {order.orderStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`om-payment-badge om-payment-${order.isPaid ? 'paid' : 'unpaid'}`}>
                      {order.paymentStatus}
                    </span>
                  </td>
                  <td>{order.date}</td>
                  <td>
                    <div className="om-action-buttons">
                      <button
                        type="button"
                        className="om-btn-icon om-btn-view"
                        onClick={() => viewOrder(order.id)}
                        title="View Order"
                        aria-label="View order"
                      >
                        <Eye {...iconProps} />
                      </button>
                      { order.orderStatus !== 'Cancelled' && order.orderStatus !== 'Delivered' && <button
                        type="button"
                        className="om-btn-icon om-btn-edit"
                        onClick={() => editOrderStatus(order.id)}
                        title="Update Status"
                        aria-label="Update order status"
                      >
                        <Pencil {...iconProps} />
                      </button>}
                      {order.orderStatus !== 'Cancelled' && order.orderStatus !== 'Delivered' && <button
                        type="button"
                        className="om-btn-icon om-btn-delete"
                        onClick={() => deleteOrder(order.id)}
                        title="Cancel Order"
                        aria-label="Cancel order"
                      >
                        <Trash2 {...iconProps} />
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {isModalOpen && currentViewingOrder && (
        <div className="om-modal-overlay" onClick={closeModal}>
          <div className="om-modal" onClick={(e) => e.stopPropagation()}>
            <div className="om-modal-header">
              <h2 className="om-modal-title">Order Details</h2>
              <button className="om-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="om-modal-content">
              {/* Order Info Section */}
              <div className="om-order-detail-section">
                <h3 className="om-section-title">
                  <ClipboardList {...iconProps} className="om-section-title-icon" aria-hidden />
                  Order Information
                </h3>
                <div className="om-detail-grid">
                  <div className="om-detail-item">
                    <div className="om-detail-label">Order ID</div>
                    <div className="om-detail-value">{currentViewingOrder.orderId}</div>
                  </div>
                  <div className="om-detail-item">
                    <div className="om-detail-label">Order Date</div>
                    <div className="om-detail-value">
                      {new Date(currentViewingOrder.createdAt || currentViewingOrder.date).toLocaleString()}
                    </div>
                  </div>
                  <div className="om-detail-item">
                    <div className="om-detail-label">Order Status</div>
                    <div className="om-detail-value">
                      <span className={`om-status-badge om-status-${currentViewingOrder.orderStatus.toLowerCase()}`}>
                        {currentViewingOrder.orderStatus}
                      </span>
                    </div>
                  </div>
                  <div className="om-detail-item">
                    <div className="om-detail-label">Payment Status</div>
                    <div className="om-detail-value">
                      <span className={`om-payment-badge om-payment-${currentViewingOrder.isPaid ? 'paid' : 'unpaid'}`}>
                        {currentViewingOrder.paymentStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Info Section */}
              <div className="om-order-detail-section">
                <h3 className="om-section-title">
                  <User {...iconProps} className="om-section-title-icon" aria-hidden />
                  Customer Information
                </h3>
                <div className="om-detail-grid">
                  <div className="om-detail-item">
                    <div className="om-detail-label">Customer Name</div>
                    <div className="om-detail-value">{currentViewingOrder.customer.name}</div>
                  </div>
                  <div className="om-detail-item">
                    <div className="om-detail-label">Email</div>
                    <div className="om-detail-value">{currentViewingOrder.customer.email}</div>
                  </div>
                  <div className="om-detail-item">
                    <div className="om-detail-label">Phone</div>
                    <div className="om-detail-value">{currentViewingOrder.customer.phone}</div>
                  </div>
                </div>
              </div>

              {/* Shipping Address Section */}
              {currentViewingOrder.shippingInfo && (
                <div className="om-order-detail-section">
                  <h3 className="om-section-title">
                    <MapPin {...iconProps} className="om-section-title-icon" aria-hidden />
                    Shipping Address
                  </h3>
                  <div className="om-detail-item">
                    <div className="om-detail-value">
                      <strong>{currentViewingOrder.shippingInfo.fullName}</strong><br/>
                      {currentViewingOrder.shippingInfo.addressLine1}<br/>
                      {currentViewingOrder.shippingInfo.addressLine2 && (
                        <>{currentViewingOrder.shippingInfo.addressLine2}<br/></>
                      )}
                      {currentViewingOrder.shippingInfo.city}, {currentViewingOrder.shippingInfo.state}<br/>
                      {currentViewingOrder.shippingInfo.pincode}, {currentViewingOrder.shippingInfo.country}<br/>
                      Phone: {currentViewingOrder.shippingInfo.phone}
                    </div>
                  </div>
                </div>
              )}

              {/* Order Items Section */}
              <div className="om-order-detail-section">
                <h3 className="om-section-title">
                  <ShoppingBag {...iconProps} className="om-section-title-icon" aria-hidden />
                  Order Items
                </h3>
                <table className="om-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Size</th>
                      <th>Specifications</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Tax %</th>
                      <th>Line subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentViewingOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td>
                          {item.bagSize.cm} cm<br/>
                          {item.bagSize.inch} inch
                        </td>
                        <td>
                          {item.specifications.gsm} GSM<br/>
                          {item.specifications.bf} BF
                        </td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price.amount}/{item.price.unit}</td>
                        <td>
                          {item.taxPercentage != null && item.taxPercentage !== ''
                            ? `${Number(item.taxPercentage)}%`
                            : '—'}
                        </td>
                        <td>₹{(item.quantity * item.price.amount).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Order Totals Section */}
              <div className="om-order-detail-section">
                <h3 className="om-section-title">
                  <IndianRupee {...iconProps} className="om-section-title-icon" aria-hidden />
                  Order Summary
                </h3>
                <div className="om-totals-section">
                  <div className="om-total-row">
                    <span>Subtotal:</span>
                    <span>₹{currentViewingOrder.totals.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="om-total-row">
                    <span>Shipping:</span>
                    <span>₹{currentViewingOrder.totals.shipping.toLocaleString()}</span>
                  </div>
                  <div className="om-total-row">
                    <span>Tax:</span>
                    <span>₹{currentViewingOrder.totals.tax.toLocaleString()}</span>
                  </div>
                  <div className="om-total-row">
                    <span>Discount:</span>
                    <span>₹{currentViewingOrder.totals.discount.toLocaleString()}</span>
                  </div>
                  <div className="om-total-row om-grand">
                    <span>Grand Total:</span>
                    <span>₹{currentViewingOrder.totals.grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details Section */}
              {currentViewingOrder.payment && (
                <div className="om-order-detail-section">
                  <h3 className="om-section-title">
                    <CreditCard {...iconProps} className="om-section-title-icon" aria-hidden />
                    Payment Information
                  </h3>
                  <div className="om-detail-grid">
                    <div className="om-detail-item">
                      <div className="om-detail-label">Payment Provider</div>
                      <div className="om-detail-value">{currentViewingOrder.payment.provider}</div>
                    </div>
                    <div className="om-detail-item">
                      <div className="om-detail-label">Payment ID</div>
                      <div className="om-detail-value">{currentViewingOrder.payment.payPaymentId || 'N/A'}</div>
                    </div>
                    <div className="om-detail-item">
                      <div className="om-detail-label">Order ID</div>
                      <div className="om-detail-value">{currentViewingOrder.payment.payOrderId || 'N/A'}</div>
                    </div>
                    <div className="om-detail-item">
                      <div className="om-detail-label">Paid Amount</div>
                      <div className="om-detail-value">
                        ₹{(currentViewingOrder.payment.amount || currentViewingOrder.totals.grandTotal).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Update Section */}
              <div className="om-order-detail-section">
                <h3 className="om-section-title">
                  <RefreshCw {...iconProps} className="om-section-title-icon" aria-hidden />
                  Update Order Status
                </h3>
                <div className="om-status-update-form">
                  <select
                    className="om-filter-select"
                    value={currentViewingOrder.orderStatus}
                    onChange={(e) => updateOrderState(e.target.value)}
                    disabled={currentViewingOrder.orderStatus === 'Delivered' || currentViewingOrder.orderStatus === 'Cancelled'}
                  >
                    {getValidNextStatuses(currentViewingOrder.orderStatus).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  {(currentViewingOrder.orderStatus === 'Delivered' || currentViewingOrder.orderStatus === 'Cancelled') && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      This order is in a terminal state and cannot be modified.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="om-modal-actions">
              <button type="button" className="om-btn-cancel" onClick={closeModal}>Close</button>
              <button type="button" className="om-btn-primary" onClick={handlePrintInvoice}>
                <Printer {...iconProps} className="om-btn-leading-icon" aria-hidden />
                Print Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;